<template>
    <OpenableMenu>
        <template v-slot:label>
            Getoonde tellingen <span class="activeFilterBadge">{{ totalHourBalanceFiltersActive }}</span>
        </template>
        <template v-slot:content>
            <List>
                <template v-for="(type, index) in balanceFilter" :key="index">
                    <ListItem @click.stop>
                        <CqToggle v-model="type.active" @update:modelValue="handleFilter" :label="type.longCaption" />
                    </ListItem>
                </template>
            </List>
        </template>
    </OpenableMenu>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import {CqToggle} from '@cquential/ui';
    import OpenableMenu from '@/components/Branding/OpenableMenu.vue';
    import ListItem from '@/components/Branding/ListItem.vue';
    import List from '@/components/Branding/List.vue';
    import {BalanceFilterType, BalanceFilterEnabled, BalanceFilterOption} from '@/components/FilterBar/types';
    import {DeploymentCountConfiguration, LeaveOverview, TimeType} from '@cquential/api';

    @Component({
        components: {
            CqToggle,
            OpenableMenu,
            ListItem,
            List,
        },
        emits: ['update:enabledBalanceFilters'],
    })
    export default class VisibleBalancesMenu extends Vue {
        @Prop({required: true}) public timeTypes!: Record<number, TimeType>;
        @Prop({required: true}) public enabledBalanceFilters!: Record<string, BalanceFilterEnabled>;
        @Prop({required: true}) public leaveOverviews!: LeaveOverview[];
        @Prop({required: true}) public deploymentCountConfigurations!: DeploymentCountConfiguration[];

        public balanceFilter: BalanceFilterOption[] = [];

        public mounted(): void {
            this.buildHourBalanceFilter();
        }

        public get totalHourBalanceFiltersActive(): number {
            let activeCount: number = 0;
            for (const i in this.balanceFilter) {
                activeCount += (this.balanceFilter[i].active) ? 1 : 0;
            }

            return activeCount;
        }

        private buildHourBalanceFilter(): void {
            const newBalanceFilter: BalanceFilterOption[] = [];
            for (const index in this.timeTypes) {
                if (this.timeTypes[index].isLeaveCard) {
                    continue;
                }
                const typeId: string = BalanceFilterType.HourBalance + '_' + this.timeTypes[index].id;

                newBalanceFilter.push({
                    id: this.timeTypes[index].id,
                    type: BalanceFilterType.HourBalance,
                    caption: this.timeTypes[index].caption,
                    longCaption: this.timeTypes[index].longCaption,
                    active: this.enabledBalanceFilters[typeId] ? this.enabledBalanceFilters[typeId].enabled : false,
                });
            }

            for (const leaveOverview of this.leaveOverviews) {
                if (leaveOverview.id) {
                    const typeId: string = BalanceFilterType.LeaveOverview + '_' + leaveOverview.id;

                    newBalanceFilter.push({
                        id: leaveOverview.id,
                        type: BalanceFilterType.LeaveOverview,
                        caption: leaveOverview.name,
                        longCaption: leaveOverview.name,
                        active: this.enabledBalanceFilters[typeId] ? this.enabledBalanceFilters[typeId].enabled : false,
                    });
                }
            }
            for (const deploymentCountConfiguration of this.deploymentCountConfigurations) {
                const typeId: string = BalanceFilterType.DeploymentCount + '_' + deploymentCountConfiguration.id;

                newBalanceFilter.push({
                    id: deploymentCountConfiguration.id ?? -1,
                    type: BalanceFilterType.DeploymentCount,
                    caption: deploymentCountConfiguration.name,
                    longCaption: deploymentCountConfiguration.name,
                    active: this.enabledBalanceFilters[typeId] ? this.enabledBalanceFilters[typeId].enabled : false,
                });
            }

            this.balanceFilter = newBalanceFilter;
        }

        public handleFilter(): void {
            const enabledBalanceFilters: Record<string, BalanceFilterEnabled> = {};
            for (const filter of this.balanceFilter) {
                const typeId: string = filter.type + '_' + filter.id;

                if (!enabledBalanceFilters[typeId]) {
                    enabledBalanceFilters[typeId] = {
                        id: filter.id,
                        type: filter.type,
                        enabled: filter.active,
                    };
                }
            }
            this.$emit('update:enabledBalanceFilters', enabledBalanceFilters);
        }
    }
</script>

<style lang="scss" scoped>
    .activeFilterBadge {
        display: inline-block;
        width: 1.1rem;
        height: 1.1rem;
        border-radius: 50%;
        line-height: 1.1rem;
        padding-top: 1px;
        vertical-align: top;
        background: var(--annotation-background);
        color: var(--annotation-text-color);
        text-align: center;
        font-weight: 700;
    }
</style>
